import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { useUserPreferences } from '@/hooks/useUserPreferences'
import { UserPreferences } from '@/types/preferences'
import { isCookieConsentAccepted } from '@/components/CookieConsent'

const PRIMARY_THEMES = {
  blue: {
    tokens: {
      base: '#0066CC',
      strong: '#0055B3',
      soft: 'rgba(0, 102, 204, 0.08)',
      muted: 'rgba(0, 102, 204, 0.06)',
      on: '#ffffff'
    },
    palette: { 50: '#E5F2FF', 100: '#CCE5FF', 500: '#0066CC', 600: '#0055B3', 700: '#004499' }
  },
  indigo: {
    tokens: {
      base: '#6366f1',
      strong: '#4c1d95',
      soft: 'rgba(99, 102, 241, 0.14)',
      muted: 'rgba(99, 102, 241, 0.1)',
      on: '#eef2ff'
    },
    palette: { 50: '#eef2ff', 100: '#e0e7ff', 500: '#6366f1', 600: '#5b21b6', 700: '#4c1d95' }
  },
  purple: {
    tokens: {
      base: '#9333ea',
      strong: '#7c3aed',
      soft: 'rgba(147, 51, 234, 0.14)',
      muted: 'rgba(147, 51, 234, 0.1)',
      on: '#faf5ff'
    },
    palette: { 50: '#faf5ff', 100: '#f3e8ff', 500: '#9333ea', 600: '#7c3aed', 700: '#6b21a8' }
  },
  emerald: {
    tokens: {
      base: '#10b981',
      strong: '#047857',
      soft: 'rgba(16, 185, 129, 0.18)',
      muted: 'rgba(16, 185, 129, 0.12)',
      on: '#ecfdf5'
    },
    palette: { 50: '#ecfdf5', 100: '#d1fae5', 500: '#10b981', 600: '#059669', 700: '#047857' }
  },
  teal: {
    tokens: {
      base: '#14b8a6',
      strong: '#0f766e',
      soft: 'rgba(20, 184, 166, 0.18)',
      muted: 'rgba(20, 184, 166, 0.12)',
      on: '#f0fdfa'
    },
    palette: { 50: '#f0fdfa', 100: '#ccfbf1', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e' }
  },
  slate: {
    tokens: {
      base: '#64748b',
      strong: '#334155',
      soft: 'rgba(100, 116, 139, 0.18)',
      muted: 'rgba(100, 116, 139, 0.12)',
      on: '#f8fafc'
    },
    palette: { 50: '#f8fafc', 100: '#f1f5f9', 500: '#64748b', 600: '#475569', 700: '#334155' }
  }
} as const

const ACCENT_THEMES = {
  orange: {
    tokens: {
      base: '#FF9500',
      strong: '#E08600',
      soft: 'rgba(255, 149, 0, 0.12)',
      on: '#ffffff'
    },
    palette: { 50: '#FFF4E5', 100: '#FFE9CC', 500: '#FF9500', 600: '#E08600', 700: '#CC7700' }
  },
  amber: {
    tokens: {
      base: '#FFCC00',
      strong: '#E6B800',
      soft: 'rgba(255, 204, 0, 0.12)',
      on: '#ffffff'
    },
    palette: { 50: '#FFFAE5', 100: '#FFF5CC', 500: '#FFCC00', 600: '#E6B800', 700: '#CCA300' }
  },
  rose: {
    tokens: {
      base: '#f43f5e',
      strong: '#be123c',
      soft: 'rgba(244, 63, 94, 0.16)',
      on: '#fff1f2'
    },
    palette: { 50: '#fff1f2', 100: '#ffe4e6', 500: '#f43f5e', 600: '#e11d48', 700: '#be123c' }
  },
  pink: {
    tokens: {
      base: '#ec4899',
      strong: '#be185d',
      soft: 'rgba(236, 72, 153, 0.18)',
      on: '#fdf2f8'
    },
    palette: { 50: '#fdf2f8', 100: '#fce7f3', 500: '#ec4899', 600: '#db2777', 700: '#be185d' }
  },
  cyan: {
    tokens: {
      base: '#06b6d4',
      strong: '#0e7490',
      soft: 'rgba(6, 182, 212, 0.18)',
      on: '#ecfeff'
    },
    palette: { 50: '#ecfeff', 100: '#cffafe', 500: '#06b6d4', 600: '#0891b2', 700: '#0e7490' }
  },
  lime: {
    tokens: {
      base: '#84cc16',
      strong: '#4d7c0f',
      soft: 'rgba(132, 204, 22, 0.18)',
      on: '#f7fee7'
    },
    palette: { 50: '#f7fee7', 100: '#ecfccb', 500: '#84cc16', 600: '#65a30d', 700: '#4d7c0f' }
  }
} as const

type ThemeMode = 'light' | 'dark' | 'system'

const THEME_MODE_STORAGE_KEY = 'sergio-theme-mode'

const resolveStoredThemeMode = (): ThemeMode => {
  if (typeof window === 'undefined') {
    return 'system'
  }

  // Only read from localStorage if consent is granted
  if (!isCookieConsentAccepted()) {
    return 'system'
  }

  const stored = window.localStorage.getItem(THEME_MODE_STORAGE_KEY)
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored
  }

  return 'system'
}

const getSystemPrefersDark = () => {
  if (typeof window === 'undefined') {
    return false
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

type PrimaryThemeKey = keyof typeof PRIMARY_THEMES
type AccentThemeKey = keyof typeof ACCENT_THEMES

interface ThemeContextType {
  isDarkMode: boolean
  themeMode: ThemeMode
  primaryColor: string
  accentColor: string
  sidebarCollapsed: boolean
  preferences: UserPreferences | null
  updateTheme: (primaryColor: string, accentColor: string) => Promise<boolean>
  toggleSidebar: () => Promise<boolean>
  setThemeMode: (mode: ThemeMode) => void
  toggleDarkMode: () => void
  applyTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

interface ThemeProviderProps {
  children: React.ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { preferences, updatePreferences } = useUserPreferences()
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => resolveStoredThemeMode())
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => getSystemPrefersDark())

  // Use default values during logout or when preferences are null
  // This prevents CSS variables from becoming undefined
  const primaryColor = preferences?.theme_primary_color || 'blue'
  const accentColor = preferences?.theme_accent_color || 'orange'
  const sidebarCollapsed = preferences?.sidebar_collapsed || false

  const isDarkMode = useMemo(() => {
    return themeMode === 'dark' || (themeMode === 'system' && systemPrefersDark)
  }, [themeMode, systemPrefersDark])

  // Apply theme to document root - NOT wrapped in useCallback to avoid circular deps
  const applyTheme = () => {
    if (typeof document === 'undefined') {
      return
    }

    const root = document.documentElement

    // Always ensure we have valid theme values (use defaults if needed)
    const safePrimaryColor = primaryColor || 'blue'
    const safeAccentColor = accentColor || 'orange'

    // Remove existing theme classes
    root.classList.remove(
      'theme-blue', 'theme-indigo', 'theme-purple', 'theme-emerald', 'theme-teal', 'theme-slate',
      'accent-orange', 'accent-amber', 'accent-rose', 'accent-pink', 'accent-cyan', 'accent-lime'
    )

    // Apply new theme classes
    root.classList.add(`theme-${safePrimaryColor}`)
    root.classList.add(`accent-${safeAccentColor}`)

    // Apply dark mode if enabled
    root.classList.toggle('dark', isDarkMode)
    root.setAttribute('data-theme', isDarkMode ? 'dark' : 'light')
    root.style.setProperty('color-scheme', isDarkMode ? 'dark' : 'light')

    // Apply CSS custom properties for dynamic theming
    const primaryTheme = PRIMARY_THEMES[safePrimaryColor as PrimaryThemeKey] || PRIMARY_THEMES.blue
    const accentTheme = ACCENT_THEMES[safeAccentColor as AccentThemeKey] || ACCENT_THEMES.orange

    root.style.setProperty('--color-primary', primaryTheme.tokens.base)
    root.style.setProperty('--color-primary-strong', primaryTheme.tokens.strong)
    root.style.setProperty('--color-primary-soft', primaryTheme.tokens.soft)
    root.style.setProperty('--color-primary-muted', primaryTheme.tokens.muted)
    root.style.setProperty('--color-on-primary', primaryTheme.tokens.on)

    Object.entries(primaryTheme.palette).forEach(([shade, value]) => {
      root.style.setProperty(`--color-primary-${shade}`, value)
    })

    root.style.setProperty('--color-accent', accentTheme.tokens.base)
    root.style.setProperty('--color-accent-strong', accentTheme.tokens.strong)
    root.style.setProperty('--color-accent-soft', accentTheme.tokens.soft)
    root.style.setProperty('--color-on-accent', accentTheme.tokens.on)

    Object.entries(accentTheme.palette).forEach(([shade, value]) => {
      root.style.setProperty(`--color-accent-${shade}`, value)
    })
  }

  const setThemeModePreference = useCallback((mode: ThemeMode) => {
    setThemeMode(mode)
  }, [])

  const toggleDarkMode = useCallback(() => {
    setThemeMode(prev => {
      if (prev === 'system') {
        return systemPrefersDark ? 'light' : 'dark'
      }

      return prev === 'dark' ? 'light' : 'dark'
    })
  }, [systemPrefersDark])

  // Update theme colors
  const updateTheme = async (newPrimaryColor: string, newAccentColor: string): Promise<boolean> => {
    const success = await updatePreferences({
      theme_primary_color: newPrimaryColor,
      theme_accent_color: newAccentColor
    })
    if (success) {
      applyTheme()
    }
    return success
  }

  // Toggle sidebar collapsed state
  const toggleSidebar = async (): Promise<boolean> => {
    const success = await updatePreferences({
      sidebar_collapsed: !sidebarCollapsed
    })
    return success
  }

  // Apply theme when preferences, colors, or dark mode change
  useEffect(() => {
    applyTheme()
    // ESLint: applyTheme is intentionally not in deps to avoid circular dependency
    // It's a regular function that uses the current values of primaryColor, accentColor, isDarkMode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryColor, accentColor, isDarkMode, preferences])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    // Only persist theme mode if consent is granted
    if (!isCookieConsentAccepted()) {
      return
    }

    if (themeMode === 'system') {
      window.localStorage.removeItem(THEME_MODE_STORAGE_KEY)
    } else {
      window.localStorage.setItem(THEME_MODE_STORAGE_KEY, themeMode)
    }
  }, [themeMode])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = (event: MediaQueryListEvent) => {
      setSystemPrefersDark(event.matches)
    }

    setSystemPrefersDark(mediaQuery.matches)
    mediaQuery.addEventListener('change', handleChange)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  const value: ThemeContextType = {
    isDarkMode,
    themeMode,
    primaryColor,
    accentColor,
    sidebarCollapsed,
    preferences,
    updateTheme,
    toggleSidebar,
    setThemeMode: setThemeModePreference,
    toggleDarkMode,
    applyTheme
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}
